import { Box, Typography, CircularProgress, Backdrop, Card, CardContent, Chip, Tooltip } from "@mui/material";
import { AssignmentTurnedIn, AttachMoney, CalendarMonth, PersonOutline, Place } from "@mui/icons-material";
import { Desdobramento } from "../types/negocio/Desdobramento";
import ProtectedBasicMenu from "./ProtectedBasicMenu";

interface CardDesdobramentoProps {
    desdobramento: Desdobramento,
    onEdit: () => void,
    onDelete: () => void,
    onViewMore?: () => void,
    isLoading?: boolean,
}

const getSituacaoBarColor = (situacao: string): string => {
    switch (situacao) {
        case 'Pendente': return '#EF443C';
        case 'Em Andamento': return '#042E4B';
        case 'Finalizada': return '#2E7D32';
        default: return '#9e9e9e';
    }
};

export default function CardDesdobramento({ 
    desdobramento, 
    onEdit, 
    onDelete, 
    onViewMore,
    isLoading = false 
}: CardDesdobramentoProps) {
    return (
        <Card
            sx={{
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 6,
                },
                border: '1px solid',
                borderColor: 'divider',
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '8px',
            }}
        >
            {/* Loading overlay */}
            {isLoading && (
                <Backdrop
                    open={true}
                    sx={{
                        position: 'absolute',
                        zIndex: 10,
                        borderRadius: '8px',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: '100%',
                        top: 0,
                        left: 0
                    }}
                >
                    <CircularProgress color="primary" size={30} />
                </Backdrop>
            )}

            {/* Barra de cor do status */}
            <Box 
                sx={{ 
                    height: '6px', 
                    bgcolor: getSituacaoBarColor(desdobramento.situacaoKanban),
                    width: '100%',
                }} 
            />

            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2, '&:last-child': { pb: 2 } }}>
                {/* Header: Status chip + Menu */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Chip
                        label={desdobramento.situacaoKanban || 'Sem status'}
                        size="small"
                        sx={{ fontWeight: 'bold', fontSize: '0.7rem', height: '24px', bgcolor: getSituacaoBarColor(desdobramento.situacaoKanban), color: 'white' }}
                    />
                    <ProtectedBasicMenu
                        tela="tarefas"
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onViewMore={onViewMore}
                        fontSize="small"
                        disabled={isLoading}
                    />
                </Box>

                {/* Título da tarefa */}
                <Typography 
                    variant="body1" 
                    sx={{ 
                        fontWeight: 'bold',
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minHeight: '40px',
                    }}
                >
                    {desdobramento.oque}
                </Typography>

                {/* Plano de Ação (se disponível) */}
                {desdobramento.planoAcaoNome && (
                    <Tooltip title={`Ação: ${desdobramento.planoAcaoNome}`} arrow>
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                color: '#042E4B',
                                fontStyle: 'italic',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontSize: '0.72rem',
                            }}
                        >
                            <AssignmentTurnedIn sx={{ fontSize: '0.9rem', color: '#042E4B' }} /> {desdobramento.planoAcaoNome}
                        </Typography>
                    </Tooltip>
                )}

                {/* Info rows */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 'auto' }}>
                    {/* Responsável + Data */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
                            <PersonOutline sx={{ fontSize: '1rem', color: '#042E4B' }} />
                            <Typography 
                                variant="caption" 
                                color="#042E4B"
                                sx={{ 
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '120px'
                                }}
                            >
                                {desdobramento.quemNomeCompleto || '-'}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarMonth sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                                {desdobramento.quando || '-'}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Custo + Local */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Chip
                            icon={<AttachMoney sx={{ fontSize: '0.9rem !important' }} />}
                            label={desdobramento.quantoCusta ? `R$ ${desdobramento.quantoCusta}` : 'Gratuito'}
                            size="small"
                            color={desdobramento.quantoCusta ? "success" : "default"}
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: '22px' }}
                        />
                        {desdobramento.onde && (
                            <Tooltip title={desdobramento.onde} arrow>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                    <Place sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                                    <Typography 
                                        variant="caption" 
                                        sx={{ 
                                            color: 'text.secondary',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            maxWidth: '100px',
                                            fontSize: '0.7rem',
                                        }}
                                    >
                                        {desdobramento.onde}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    )
}